// @flow
import React, { useRef, useImperativeHandle,forwardRef } from 'react';
import { createUseStyles } from 'react-jss';
import { gql, useMutation } from '@apollo/client';
import dateFormat from 'dateformat';
import { optimisticFilesData, sanitizeFilesData } from './assetUtils';
import { getS3AssetsManagement } from 'shared/lib/clientSettings';
import ImageCropper from './ImageCropper';

type Props = {
  path: string,
  imageUrl: string,
  outputType: string,
  croppers: [{
    name: string,
    description: string,
    aspectratio: string,
  }]
}

const UPLOAD_FILES = gql`
  mutation uploadFiles($files: [Upload!]!, $path: String!, $size: Int!, $autoConvert: Boolean) {
    uploadFiles(files: $files, path: $path, size: $size, autoConvert: $autoConvert) {
      Key
      Name: Key
      Size
      LastModified
      __typename
    }
  }
`;

const ImageCropperManager = forwardRef((props: Props, ref: Reference) =>{
  const {path, imageUrl, outputType, croppers: inputCroppers} = props;
  const c = useStyles();
  const cropperRefs = useRef([]);

  const croppers = inputCroppers?.length > 0 ? inputCroppers : [];

  const [uploadFilesMutation] = useMutation(UPLOAD_FILES);

  const uploadFiles = async (filePath, files) => {
    const sanitizedFiles = sanitizeFilesData(files, filePath);

    // Invalid files are stripped out from the list
    if (sanitizedFiles.length !== files.length) {
      console.error('Only images below 10MB and videos below 300MB can be uploaded');
      return;
    }

    // No files to upload
    if (!sanitizedFiles.length) {
      return;
    }

    const optimisticFiles = optimisticFilesData(sanitizedFiles);
    try {
      await uploadFilesMutation({
        variables: {
          files: sanitizedFiles,
          path: filePath,
          size: sanitizedFiles[0].size,
          autoConvert: false,
        },
        optimisticResponse: {
          uploadFiles: optimisticFiles
        },
      });
    } catch (e) {
      console.error('Failed to upload files', e);
    }
  }

  useImperativeHandle(ref, () => ({
    getCroppedFiles: async () => {
      const croppedFiles = {};
      const imageFileName = imageUrl.substring(imageUrl.lastIndexOf('/') + 1);
      const imageFileNameWithoutExtension = imageFileName.substring(0, imageFileName.lastIndexOf('.'))
      const now = dateFormat(new Date(), 'yyyymmddHHMMss');
      const folderPath = `${imageFileNameWithoutExtension}-cropped/${now}/`;

      // Wrap in promises to await all async updates
      const promises = cropperRefs.current?.map(r =>
        new Promise((resolve) => {
          r.getCroppedFile(f => {
              uploadFiles(`${path}${folderPath}`, [f.file]);

              let finalFilePath = `${path}${folderPath}${f.file.name}`;
              finalFilePath = `${getS3AssetsManagement() || ''}/${encodeURI(finalFilePath)}`;
              croppedFiles[f.name] = finalFilePath;
              resolve();
          });
        })
      );

      await Promise.all(promises);

      return croppedFiles;
    }
  }));

  return (
    <div className={c.container}>
      <h2 className={c.title}>Image Cropper</h2>
      <div className={c.description}>
        Scroll down to adjust the image for other screens.
      </div>
      <hr />
      {
        croppers.map((c, i) => (
          <ImageCropper key={`cropper-${i}`} ref={c => cropperRefs.current.push(c)} imageUrl={imageUrl} outputType={outputType} {...c} />
        ))
      }
    </div>
  );
});

ImageCropperManager.displayName = 'ImageCroppers';

const useStyles = createUseStyles({
  container: {
    width: 'fit-content',
    margin: '30px auto'
  },
  title: {
    marginBlockStart: 0,
  },
  description: {
    marginBottom: '20px'
  }
});

export default ImageCropperManager;
